/* eslint react-hooks/exhaustive-deps: 0 */
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import React, { useEffect, useState } from "react";
import { fmtDate as dtFmtDate } from "@/lib/dt";   // STEP 3 · B-7c
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Stamp, CalendarIcon, Check, FileText } from "lucide-react";

const CODES = [
  "rational_purpose", "framework_acceptable",
  "mgmt_acknowledges_responsibilities", "ethics_independence_met",
  "information_expected_available", "no_scope_limitation",
];
// STEP 3 · B-7c: the one formatter (lib/dt)
function fmtDate(s){if(!s)return"—";return dtFmtDate(s);}
function toYmd(d){if(!d)return"";const y=d.getFullYear();const m=String(d.getMonth()+1).padStart(2,"0");const dd=String(d.getDate()).padStart(2,"0");return `${y}-${m}-${dd}`;}

export default function W171AcceptancePage() {
  const { current, currentEngId } = useEngagement();
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const [signDate, setSignDate] = useState("");
  const [open, setOpen] = useState(false);

  const reload = async () => {
    if (!currentEngId) return;
    const r = await api.get(`/engagements/${currentEngId}/w17/acceptance`, { _silent: true });
    setState(r.data);
    if (r.data?.signed_off_at) setSignDate((r.data.signed_off_at||"").slice(0,10));
  };
  useEffect(() => { reload(); }, [currentEngId]);

  const toggle = async (code, met) => {
    try {
      await api.post(`/engagements/${currentEngId}/w17/acceptance/precondition`,
        { code, met }, { _silent: true });
      await reload();
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  const signOff = async () => {
    try {
      await api.post(`/engagements/${currentEngId}/w17/acceptance/signoff`,
        { signed_at: signDate || new Date().toISOString().slice(0,10) },
        { _silent: true });
      await reload();
      toast.success(t("w17_1.signedOk"));
    } catch (e) { toast.error(toErrMsg(e, t("w17_1.signBlocked"))); }
  };

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;
  if (!state) return <div className="text-sm text-muted-foreground p-6" data-testid="w17-1-loading">{t("common.loading")}</div>;

  const isReview = (current?.engagement_type || "audit") === "review";
  if (!isReview) return (
    <div className="p-6 text-sm text-muted-foreground" data-testid="w17-1-audit-only">
      {t("w17_1.notForAudit")}
    </div>
  );

  const preList = CODES.map(c => ({ code: c, ...(state.preconditions[c]||{}) }));
  const allMet = state.all_met;

  return (
    <div className="space-y-6" data-testid="w17-1-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="w17-1-title">
          <span dir="ltr" className="font-mono text-[13px] text-muted-foreground me-2">501(r)</span>
          {t("w17_1.title")}
        </h1>
        <SignOffBar itemType="w17_1" itemKey="W17.1" />   {/* Batch 11 · A2 */}
        <p className="text-sm text-muted-foreground">{t("w17_1.subtitle")}</p>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w17_1.preconditions")}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {preList.map((p, i) => (
            <label key={p.code} className="flex items-start gap-2 text-[13px] cursor-pointer"
                     data-testid={`w17-1-pre-${p.code}`}>
              <Checkbox checked={!!p.met} onCheckedChange={(v)=>toggle(p.code, !!v)}
                            data-testid={`w17-1-pre-${p.code}-cb`} />
              <div className="flex-1">
                <div>{t(`w17_1.pre.${p.code}`)}</div>
                {p.met && (
                  <div className="text-[10px] text-muted-foreground" data-testid={`w17-1-pre-${p.code}-meta`}>
                    {t("w17_1.metBy").replace("{name}", p.by_name || "—").replace("{date}", fmtDate(p.at))}
                  </div>
                )}
              </div>
            </label>
          ))}
          <div className={`mt-3 rounded border p-2 text-[12px] ${allMet ? "bg-emerald-50 text-emerald-900" : "bg-amber-50 text-amber-900"}`}
                data-testid="w17-1-acceptance-banner">
            {allMet ? t("w17_1.acceptanceOk") : t("w17_1.acceptanceBlocked")}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base normal-case" data-testid="w17-1-terms-title">{t("w17_1.termsTitle")}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <TermsRow state={state} currentEngId={currentEngId} t={t} reload={reload} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base normal-case">{t("w17_1.signOff")}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {state.signed_off ? (
            <div className="text-[12px] text-emerald-900 flex items-center gap-1" data-testid="w17-1-signed-badge">
              <Check className="h-3 w-3" />
              {t("w17_1.signedBy").replace("{name}", state.signed_off_by_name || "—").replace("{date}", fmtDate(state.signed_off_at))}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm"
                              className="h-8 min-w-[160px] justify-start gap-1 px-2 font-mono text-[12px]"
                              data-testid="w17-1-sign-date">
                    <CalendarIcon className="h-3 w-3" />
                    <span dir="ltr">{signDate ? fmtDate(signDate) : t("w17_1.signDatePh")}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={signDate ? new Date(signDate) : undefined}
                                onSelect={(d)=>{ setOpen(false); setSignDate(toYmd(d)||""); }}
                                initialFocus />
                </PopoverContent>
              </Popover>
              <Button onClick={signOff} disabled={!allMet}
                          data-testid="w17-1-sign-btn">
                <Stamp className="h-3 w-3 me-1" />{t("w17_1.signBtn")}
              </Button>
              {!allMet && <span className="text-[11px] text-amber-900">{t("w17_1.signBlocked")}</span>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TermsRow({ state, currentEngId, t, reload }) {
  const [pending, setPending] = React.useState((state.terms_agreed_at || "").slice(0,10));
  const [pop, setPop] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const saved = !!state.terms_agreed_at;
  const save = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await api.post(`/engagements/${currentEngId}/w17/acceptance/terms`,
        { agreed_at: pending, engagement_letter_id: state.engagement_letter_id || "el-linked" },
        { _silent: true });
      await reload();
      toast.success(t("w17_1.termsSaveOk"));
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-[13px]">
        <span className="text-muted-foreground">{t("w17_1.termsAgreedAt")}:</span>
        <Popover open={pop} onOpenChange={setPop}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" data-testid="w17-1-terms-date-btn"
                     className="h-8 min-w-[9rem] justify-start gap-1 px-2 font-mono text-[12px]">
              <CalendarIcon className="h-3 w-3" />
              <span dir="ltr">{pending ? fmtDate(pending) : t("w17_1.termsDatePh")}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0">
            <Calendar mode="single" selected={pending ? new Date(pending) : undefined}
                        onSelect={(d) => { setPending(toYmd(d)); setPop(false); }} />
          </PopoverContent>
        </Popover>
        <Button size="sm" data-testid="w17-1-terms-save" onClick={save}
                 disabled={!pending || busy}>{t("w17_1.termsSaveBtn")}</Button>
        <a href="/acceptance" data-testid="w17-1-terms-el-link"
            className="inline-flex items-center gap-1 rounded border px-2 py-1 text-[12px] text-blue-700 hover:underline">
          <FileText className="h-3 w-3" />{t("w17_1.termsLetterLink")}
        </a>
      </div>
      {saved && (
        <div data-testid="w17-1-terms-saved-state"
             className="rounded border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[12px] text-emerald-900">
          {t("w17_1.termsAgreedAt")} <b>{fmtDate(state.terms_agreed_at)}</b>
          {" · " + t("w17_1.termsLetterLink") + " "}
          <span className="inline-flex items-center gap-1"><Check className="h-3 w-3" />linked</span>
        </div>
      )}
    </div>
  );
}

